import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from '@mui/material';
import {
  INITIAL_X_POSITION,
  INITIAL_Y_POSITION,
  NUMBER_OF_MAKER,
  RADIUS_OF_MAKER,
} from '../../_constants/inspectionConst';
import { GetRepairRequest, MarkerInfo } from '../../services/apiTypes';
import {
  DTA_IMAGE_SIZE,
  PRINT_REPORT_BREAK_AVOID,
} from '../../_constants/styleConst';
import { useMemo } from 'react';
import { getDtaImage } from '../../_utils/inspectionUtils';

type DtaImageProps = {
  markers: MarkerInfo[];
  repairRequest: GetRepairRequest | undefined;
};

// 診断との比較した時の表示倍率
const RATIO = 0.9;

// 帳票表示用の先端部のイメージとメモ
const ReportDtaImage: React.FC<DtaImageProps> = ({
  markers,
  repairRequest,
}) => {
  const theme = useTheme();

  const markerStyle = {
    position: 'absolute',
    width: RADIUS_OF_MAKER * 2 * RATIO,
    height: RADIUS_OF_MAKER * 2 * RATIO,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  };

  /** 先端画像の切り替え */
  const bgImage = useMemo(() => {
    return getDtaImage(repairRequest);
  }, []);

  return (
    <Grid
      container
      mt={2}
      spacing={1}
      display="flex"
      sx={PRINT_REPORT_BREAK_AVOID}
    >
      {/* マーカーエリア */}
      <Grid size={{ xs: 0, md: 1.5, lg: 0.5 }} />
      <Grid
        container
        size={{ xs: 12, md: 5, lg: 5 }}
        justifyContent="center"
        maxWidth={`calc(300px * ${RATIO}`}
      >
        <Paper
          sx={{
            bgcolor: theme.palette.background.default,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              margin: `calc(10px * ${RATIO})`,
              width: `calc(${DTA_IMAGE_SIZE} * ${RATIO})`,
              height: `calc(${DTA_IMAGE_SIZE} * ${RATIO})`,
              backgroundImage: `url(images/` + bgImage + `.png)`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          >
            {markers.map(
              (marker, i) =>
                // 動かしていないものは表示しない
                marker.x !== INITIAL_X_POSITION &&
                marker.y !== INITIAL_Y_POSITION && (
                  <Box
                    key={`marker_${i}`}
                    sx={{
                      ...markerStyle,
                      left: marker.x * RATIO,
                      top: marker.y * RATIO,
                      zIndex: NUMBER_OF_MAKER - marker.id,
                      position: 'absolute',
                    }}
                  >
                    {marker.id + 1}
                  </Box>
                ),
            )}
          </Box>
        </Paper>
      </Grid>
      <Grid size={{ xs: 0, md: 1.5, lg: 0.5 }} />
      {/* メモエリア */}
      <Grid
        size={{ xs: 12, md: 4, lg: 6 }}
        width={`calc(100% - (310px * ${RATIO})`}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {Array.from({ length: NUMBER_OF_MAKER }, (_, i) => (
                <TableRow
                  key={`marker_memo_${i}`}
                  sx={{
                    bgcolor: theme.palette.background.default,
                    height: `calc(310px * ${RATIO} / ${NUMBER_OF_MAKER})`,
                  }}
                >
                  <TableCell align="center" sx={{ p: 1, width: '5%' }}>
                    {i + 1}
                  </TableCell>
                  <TableCell sx={{ p: '5px 5px 5px 0' }}>
                    <Box
                      sx={{
                        bgcolor: theme.palette.background.default,
                        border: '1px #e0e0e0 solid',
                        borderRadius: '5px',
                        p: 1,
                        minHeight: '2rem',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {markers.find((marker) => marker.id === i)?.memo ?? ''}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default ReportDtaImage;
